/**
 * Ledger > new manual journal entry (tracker item 31).
 *
 * Date, memo and dynamic lines, each posting a debit or a credit to a
 * postable account (searchable by number or name). Totals are kept in integer
 * cents and an "out of balance by $X" guard disables submit. This is display
 * arithmetic only: the server validates and posts the entry, and its errors
 * are shown above the submit button. Manual entries post as ADJUSTMENT with a
 * generated "manual-…" sourceId.
 */

import React, { useEffect, useMemo, useRef, useState } from 'react';
import { Link, useNavigate } from 'react-router-dom';
import { createAPIClient, ICustomersApi, IGLAccountsApi, IJournalEntriesApi } from '../api';
import * as types from '../api/types';
import { getErrorMessage } from '../utils/apiError';
import { inputClass, labelClass, primaryButtonClass, rowDangerActionClass, secondaryButtonClass } from '../components/formStyles';
import {
  LEDGER_PATH,
  MIN_LINES,
  DraftLine,
  ManualEntryForm,
  accountLabel,
  buildCreateJournalEntryRequest,
  draftTotals,
  emptyDraftLine,
  emptyManualEntryForm,
  filterCustomers,
  filterPostableAccounts,
  isReceivableAccount,
  ledgerEntryPath,
  ledgerMoney,
  lineAmountError,
  localToday,
  manualSourceId,
  outOfBalanceMessage,
  setLineAmount,
  submitBlocker,
} from './ledgerUi';
import { Toast } from '../components/Toast';

const MEMO_MAX_LENGTH = 500;

export function LedgerEntryCreatePage() {
  const api = useMemo<IJournalEntriesApi & IGLAccountsApi & ICustomersApi>(() => createAPIClient(), []);
  const navigate = useNavigate();
  const keyCounter = useRef(0);
  const nextKey = () => `line-${++keyCounter.current}`;
  const [form, setForm] = useState<ManualEntryForm>(() => emptyManualEntryForm(localToday(), nextKey));
  const [accounts, setAccounts] = useState<types.GLAccount[]>([]);
  const [accountsError, setAccountsError] = useState<string | null>(null);
  const [loadingAccounts, setLoadingAccounts] = useState(true);
  const [customers, setCustomers] = useState<types.Customer[]>([]);
  const [customersError, setCustomersError] = useState<string | null>(null);
  const [submitting, setSubmitting] = useState(false);
  const [serverError, setServerError] = useState<string | null>(null);

  useEffect(() => {
    let active = true;
    api.listGLAccounts()
      .then((list) => { if (active) setAccounts(list); })
      .catch((cause) => { if (active) setAccountsError(getErrorMessage(cause, 'Could not load GL accounts')); })
      .finally(() => { if (active) setLoadingAccounts(false); });
    return () => { active = false; };
  }, [api]);

  useEffect(() => {
    let active = true;
    async function loadCustomers() {
      const all: types.Customer[] = [];
      let page = 1;
      let pages = 1;
      do {
        const result = await api.listCustomers(page, 100);
        all.push(...result.data);
        pages = result.pagination.pages;
        page++;
      } while (page <= pages);
      if (active) setCustomers(all);
    }
    loadCustomers().catch((cause) => { if (active) setCustomersError(getErrorMessage(cause, 'Could not load customers')); });
    return () => { active = false; };
  }, [api]);

  const accountsById = useMemo(() => new Map(accounts.map((account) => [account.id, account])), [accounts]);
  const postableCount = useMemo(() => accounts.filter((account) => account.isPostable).length, [accounts]);
  const totals = draftTotals(form.lines);
  const balanceMessage = outOfBalanceMessage(totals);
  const blocker = submitBlocker(form);

  function updateLine(key: string, change: (line: DraftLine) => DraftLine) {
    setForm((prev) => ({ ...prev, lines: prev.lines.map((line) => (line.key === key ? change(line) : line)) }));
    setServerError(null);
  }

  function addLine() {
    setForm((prev) => ({ ...prev, lines: [...prev.lines, emptyDraftLine(nextKey())] }));
  }

  function removeLine(key: string) {
    setForm((prev) => (prev.lines.length <= MIN_LINES ? prev : { ...prev, lines: prev.lines.filter((line) => line.key !== key) }));
  }

  async function handleSubmit(event: React.FormEvent) {
    event.preventDefault();
    if (blocker || submitting) return;
    setSubmitting(true);
    setServerError(null);
    try {
      const created = await api.createJournalEntry(buildCreateJournalEntryRequest(form, manualSourceId()));
      navigate(ledgerEntryPath(created.id));
    } catch (cause) {
      setServerError(getErrorMessage(cause, 'Could not post the journal entry'));
      setSubmitting(false);
    }
  }

  return (
    <main className="max-w-7xl mx-auto px-4 sm:px-6 lg:px-8 py-6 sm:py-8">
      <Link to={LEDGER_PATH} className="inline-block py-2 sm:py-0 text-sm font-semibold text-gray-500 hover:text-blue-600">← Ledger</Link>
      <h1 className="text-2xl font-bold text-gray-900 mt-1 mb-2">New manual journal entry</h1>
      <p className="mb-6 max-w-3xl text-sm text-gray-600">
        Posted entries cannot be edited or deleted; correct a mistake by reversing the entry. Only postable accounts can
        be used, and total debits must equal total credits.
      </p>

      {accountsError && <Toast kind="error">{accountsError}</Toast>}

      <form onSubmit={handleSubmit} noValidate className="bg-white rounded-lg shadow">
        <div className="p-5 sm:p-6 space-y-6">
          <div className="grid grid-cols-1 gap-4 sm:grid-cols-6">
            <div className="sm:col-span-2">
              <label htmlFor="entry-date" className={labelClass}>Entry date<span className="ml-0.5 text-red-600">*</span></label>
              <input
                id="entry-date"
                type="date"
                className={inputClass}
                value={form.entryDate}
                disabled={submitting}
                onChange={(event) => { setForm((prev) => ({ ...prev, entryDate: event.target.value })); setServerError(null); }}
              />
            </div>
            <div className="sm:col-span-4">
              <label htmlFor="entry-memo" className={labelClass}>Memo</label>
              <input
                id="entry-memo"
                type="text"
                className={inputClass}
                maxLength={MEMO_MAX_LENGTH}
                value={form.memo}
                disabled={submitting}
                placeholder="Move undeposited funds to cash"
                onChange={(event) => setForm((prev) => ({ ...prev, memo: event.target.value }))}
              />
            </div>
          </div>

          {loadingAccounts ? <p>Loading accounts…</p> : postableCount === 0 && !accountsError ? (
            <p role="alert" className="text-red-700">
              There are no postable GL accounts. Set up the <Link to="/settings/chart-of-accounts" className="underline">chart of accounts</Link> first.
            </p>
          ) : (
            <div className="overflow-x-auto">
              <table className="responsive-table min-w-full text-sm">
                <caption className="sr-only">Journal lines</caption>
                <thead className="bg-gray-50">
                  <tr>
                    <th scope="col" className="px-3 py-2 text-left font-medium text-gray-600">Account</th>
                    <th scope="col" className="px-3 py-2 text-right font-medium text-gray-600 w-40">Debit</th>
                    <th scope="col" className="px-3 py-2 text-right font-medium text-gray-600 w-40">Credit</th>
                    <th scope="col" className="px-3 py-2"><span className="sr-only">Remove</span></th>
                  </tr>
                </thead>
                <tbody className="divide-y divide-gray-100">
                  {form.lines.map((line, index) => {
                    const n = index + 1;
                    const options = filterPostableAccounts(accounts, line.search, line.glAccountId);
                    const amountError = lineAmountError(line);
                    const account = accountsById.get(line.glAccountId);
                    return (
                      <tr key={line.key} data-testid={`line-${n}`} className="align-top">
                        <td data-label="Account" className="px-3 py-2 md:min-w-[18rem] max-md:flex-col">
                          <div className="flex w-full flex-col gap-1 text-left sm:flex-row">
                            <input
                              type="search"
                              aria-label={`Search accounts for line ${n}`}
                              placeholder="Search number or name"
                              className={`${inputClass} sm:w-44`}
                              value={line.search}
                              disabled={submitting}
                              onChange={(event) => updateLine(line.key, (l) => ({ ...l, search: event.target.value }))}
                            />
                            <select
                              aria-label={`Account for line ${n}`}
                              className={inputClass}
                              value={line.glAccountId}
                              disabled={submitting}
                              onChange={(event) => updateLine(line.key, (l) => ({ ...l, glAccountId: event.target.value, customerId: '', customerSearch: '' }))}
                            >
                              <option value="">{options.length ? 'Choose an account' : 'No matching accounts'}</option>
                              {options.map((option) => <option key={option.id} value={option.id}>{accountLabel(option)}</option>)}
                            </select>
                          </div>
                          {isReceivableAccount(account) && (
                            <div className="mt-2 w-full text-left">
                              <div className="flex flex-col gap-1 sm:flex-row">
                                <input
                                  type="search"
                                  aria-label={`Search customers for line ${n}`}
                                  placeholder="Search customer"
                                  className={`${inputClass} sm:w-44`}
                                  value={line.customerSearch}
                                  disabled={submitting}
                                  onChange={(event) => updateLine(line.key, (l) => ({ ...l, customerSearch: event.target.value }))}
                                />
                                <select
                                  aria-label={`Customer for line ${n}`}
                                  className={inputClass}
                                  value={line.customerId}
                                  disabled={submitting}
                                  onChange={(event) => updateLine(line.key, (l) => ({ ...l, customerId: event.target.value }))}
                                >
                                  <option value="">No customer</option>
                                  {filterCustomers(customers, line.customerSearch, line.customerId).map((customer) => <option key={customer.id} value={customer.id}>{customer.name}</option>)}
                                </select>
                              </div>
                              {!line.customerId && (
                                <p className="mt-1 text-xs text-amber-700">
                                  Choose the customer this receivable belongs to. An untagged line does not count toward any customer's receivable balance.
                                </p>
                              )}
                              {customersError && <Toast kind="error">{customersError}</Toast>}
                            </div>
                          )}
                          {amountError && <p role="alert" className="mt-1 w-full text-left text-xs text-red-700">{amountError}</p>}
                        </td>
                        <td data-label="Debit" className="px-3 py-2">
                          <input
                            inputMode="decimal"
                            aria-label={`Debit for line ${n}`}
                            className={`${inputClass} text-right`}
                            value={line.debit}
                            disabled={submitting}
                            placeholder="0.00"
                            onChange={(event) => updateLine(line.key, (l) => setLineAmount(l, 'debit', event.target.value))}
                          />
                        </td>
                        <td data-label="Credit" className="px-3 py-2">
                          <input
                            inputMode="decimal"
                            aria-label={`Credit for line ${n}`}
                            className={`${inputClass} text-right`}
                            value={line.credit}
                            disabled={submitting}
                            placeholder="0.00"
                            onChange={(event) => updateLine(line.key, (l) => setLineAmount(l, 'credit', event.target.value))}
                          />
                        </td>
                        <td className="px-3 py-2 text-right">
                          <button
                            type="button"
                            className={`${rowDangerActionClass} min-h-[44px] md:min-h-0 md:mt-2.5`}
                            disabled={submitting || form.lines.length <= MIN_LINES}
                            aria-label={`Remove line ${n}`}
                            onClick={() => removeLine(line.key)}
                          >
                            Remove
                          </button>
                        </td>
                      </tr>
                    );
                  })}
                </tbody>
                <tfoot>
                  <tr className="border-t border-gray-200 font-semibold">
                    <td className="px-3 py-3">
                      <button type="button" className={`${secondaryButtonClass} w-full md:w-auto min-h-[44px] md:min-h-0`} disabled={submitting} onClick={addLine}>Add line</button>
                    </td>
                    <td data-label="Total debits" className="px-3 py-3 text-right tabular-nums" data-testid="total-debits">{ledgerMoney(totals.debitCents)}</td>
                    <td data-label="Total credits" className="px-3 py-3 text-right tabular-nums" data-testid="total-credits">{ledgerMoney(totals.creditCents)}</td>
                    <td />
                  </tr>
                </tfoot>
              </table>
            </div>
          )}

          <p
            data-testid="balance-status"
            className={`text-sm font-medium ${balanceMessage ? 'text-red-700' : totals.debitCents > 0 ? 'text-green-700' : 'text-gray-500'}`}
          >
            {balanceMessage ?? (totals.debitCents > 0 ? `Balanced: ${ledgerMoney(totals.debitCents)}` : 'Enter amounts to balance the entry.')}
          </p>
        </div>

        <div className="rounded-b-lg border-t border-gray-200 bg-gray-50 px-5 py-4 sm:px-6">
          {serverError && <Toast kind="error">{serverError}</Toast>}
          <div className="flex flex-col sm:flex-row sm:flex-wrap sm:items-center sm:justify-end gap-3">
            {blocker && blocker !== balanceMessage && <span className="text-sm text-gray-600" data-testid="submit-blocker">{blocker}</span>}
            <Link to={LEDGER_PATH} className={`${secondaryButtonClass} inline-flex min-h-[44px] items-center justify-center sm:min-h-0`}>Cancel</Link>
            <button type="submit" className={`${primaryButtonClass} min-h-[44px] sm:min-h-0`} disabled={!!blocker || submitting || loadingAccounts}>
              {submitting ? 'Posting…' : 'Post entry'}
            </button>
          </div>
        </div>
      </form>
    </main>
  );
}
